import React, { useEffect, useState } from 'react';
import { adminFetch, adminSession, whoami } from '../lib/extras';
import { authService } from '../lib/auth';

// Dvoufázové ověření administrátora (TOTP – Google Authenticator, Microsoft Authenticator, 1Password…).
// mode 'verify' = zadání kódu po přihlášení, 'setup' = založení (QR kód + záložní kódy).
interface Props {
  mode: 'verify' | 'setup' | 'error';
  email?: string;
  onDone: () => void;
  onCancel: () => void;
  optional?: boolean; // založení z administrace (zrušení neodhlašuje)
}

const inputCls = 'w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:border-red-600 outline-none text-lg font-bold tracking-[0.3em] text-center bg-gray-50';
const btnPrimary = 'w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-md transition-all disabled:opacity-50';
const btnGhost = 'w-full py-2.5 px-4 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-md border-2 border-gray-200 transition-all';

const TwoFactorModal: React.FC<Props> = ({ mode, email, onDone, onCancel, optional }) => {
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState<{ qr: string; secret: string } | null>(null);
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    if (mode !== 'setup') return;
    adminFetch<{ qr: string; secret: string }>('/api/admin/2fa/setup', { body: {} })
      .then(setSetup).catch(e => setError(e.message));
  }, [mode]);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await adminFetch<{ session: string; device: string | null }>('/api/admin/2fa/verify', { body: { code, trustDevice: trust } });
      adminSession.set(r.session, r.device);
      await authService.recheck();
      onDone();
    } catch (err: any) { setError(err.message); setCode(''); }
    finally { setBusy(false); }
  };

  const enable = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await adminFetch<{ session: string; recovery: string[] }>('/api/admin/2fa/enable', { body: { code } });
      adminSession.set(r.session);
      setRecovery(r.recovery);
    } catch (err: any) { setError(err.message); setCode(''); }
    finally { setBusy(false); }
  };

  // Po uložení záložních kódů: server musí potvrdit zapnuté a ověřené 2FA, jinak ukážeme proč to nejde dál.
  const finishSetup = async () => {
    setBusy(true); setError(null);
    try {
      const me = await whoami();
      const t = me.twofa;
      if (t && !t.enabled) {
        setError('Server dvoufázové ověření neeviduje jako zapnuté. Načtěte prosím stránku znovu a nastavení zopakujte (naskenujte nový QR kód).');
        return;
      }
      if (t && t.enabled && !t.verified) {
        setError('Ověření tohoto přihlášení se nepodařilo potvrdit. Načtěte stránku znovu a zadejte kód z aplikace.');
        return;
      }
      await authService.recheck();
      onDone();
    } catch (err: any) { setError(err?.message || 'Nepodařilo se spojit se serverem.'); }
    finally { setBusy(false); }
  };
  const cancel = async () => { if (!optional && !(mode === 'setup' && recovery)) { await authService.logout(); } onCancel(); };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 bg-red-600 text-white">
          <h2 className="text-lg font-bold">{mode === 'setup' ? 'Zapnout dvoufázové ověření' : 'Ověření přihlášení'}</h2>
          {email && <p className="text-xs opacity-90 mt-0.5">{email}</p>}
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm border border-red-200">{error}</div>}

          {mode === 'error' && (
            <>
              <p className="text-sm text-gray-700">Nepodařilo se ověřit stav přihlášení na serveru. Administrace zůstává z bezpečnostních důvodů zamčená.</p>
              <button className={btnPrimary} onClick={async () => { await authService.recheck(); onDone(); }}>Zkusit znovu</button>
              <button className={btnGhost} onClick={cancel}>Odhlásit</button>
            </>
          )}

          {mode === 'verify' && (
            <form onSubmit={verify} className="space-y-4">
              <p className="text-sm text-gray-700">{useRecovery ? 'Zadejte jeden ze záložních kódů (každý platí jen jednou).' : 'Zadejte 6místný kód z ověřovací aplikace v telefonu.'}</p>
              <input autoFocus inputMode={useRecovery ? 'text' : 'numeric'} autoComplete="one-time-code" value={code}
                onChange={e => setCode(useRecovery ? e.target.value.toUpperCase().slice(0, 11) : e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={inputCls} placeholder={useRecovery ? 'XXXXX-XXXXX' : '000000'} />
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={trust} onChange={e => setTrust(e.target.checked)} className="w-4 h-4 accent-red-600" />
                Důvěřovat tomuto zařízení 30 dní
              </label>
              <button type="submit" className={btnPrimary} disabled={busy || (!useRecovery && code.length !== 6) || (useRecovery && code.length < 10)}>{busy ? 'Ověřuji…' : 'Ověřit'}</button>
              <button type="button" className="w-full text-xs text-gray-500 hover:text-red-600 underline" onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}>
                {useRecovery ? 'Zpět na kód z aplikace' : 'Nemám telefon – použít záložní kód'}
              </button>
              <button type="button" className={btnGhost} onClick={cancel}>Odhlásit</button>
            </form>
          )}

          {mode === 'setup' && !recovery && (
            <form onSubmit={enable} className="space-y-4">
              <ol className="text-sm text-gray-700 list-decimal ml-5 space-y-1">
                <li>V telefonu otevřete ověřovací aplikaci (Google Authenticator, Microsoft Authenticator…).</li>
                <li>Naskenujte QR kód.</li>
                <li>Opište 6místný kód, který aplikace ukáže.</li>
              </ol>
              <div className="flex justify-center">{setup ? <img src={setup.qr} alt="QR kód pro ověřovací aplikaci" className="w-[200px] h-[200px]" /> : <div className="w-[200px] h-[200px] bg-gray-100 rounded-md animate-pulse" />}</div>
              {setup && <p className="text-[11px] text-gray-500 text-center break-all">Ruční zadání: <span className="font-mono font-bold text-gray-700">{setup.secret}</span></p>}
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className={inputCls} placeholder="000000" />
              <button type="submit" className={btnPrimary} disabled={busy || code.length !== 6 || !setup}>{busy ? 'Ověřuji…' : 'Zapnout'}</button>
              <button type="button" className={btnGhost} onClick={cancel}>Zrušit</button>
            </form>
          )}

          {mode === 'setup' && recovery && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700"><b>Hotovo.</b> Uložte si záložní kódy na bezpečné místo. Použijete je, když nebudete mít telefon. Každý platí jen jednou a znovu se nezobrazí.</p>
              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-md border-2 border-gray-200 font-mono text-sm font-bold text-center">
                {recovery.map(r => <span key={r}>{r}</span>)}
              </div>
              <button className={btnGhost} onClick={() => navigator.clipboard?.writeText(recovery.join('\n'))}>Zkopírovat kódy</button>
              <button className={btnPrimary} onClick={finishSetup} disabled={busy}>{busy ? 'Ověřuji…' : 'Mám uloženo, pokračovat'}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;
